// src/features/tag/components/ManageTagsDrawer.tsx

import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Drawer,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconPencil, IconSearch, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTagUsage } from '../useTagUsage';
import { useDeleteTag, useRenameTag } from '../useTagManageMutations';
import { TagManageUtils, type TagRulePlaylistRef } from '../TagManageUtils';
import { RenameTagModal } from './RenameTagModal';
import { DeleteTagModal } from './DeleteTagModal';
import { usePlaylists } from '@/features/playlists/usePlaylists';
import { useVideoGridState } from '@/features/media/stores/videoGridState';
import { useStreamGridState } from '@/features/streams/stores/streamGridState';
import { TAG_MANAGE } from '@/lib/constants/tag';
import type { TagUsage } from '@/lib/types/Tag';

const LIST_MAX_HEIGHT = '65vh';

interface ManageTagsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Account tag vocabulary manager: see every tag with usage counts, clean up
 * unused tags, rename, or cascade-delete. Opened from the Videos and Streams
 * toolbars — the vocabulary is shared across both.
 */
export function ManageTagsDrawer({ opened, onClose }: ManageTagsDrawerProps) {
  const [search, setSearch] = useState('');
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [renameTarget, setRenameTarget] = useState<TagUsage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TagUsage | null>(null);

  const navigate = useNavigate();
  const { usages, isLoading, isError } = useTagUsage({ enabled: opened });
  const { data: playlistData } = usePlaylists({ enabled: opened });
  const renameMutation = useRenameTag();
  const deleteMutation = useDeleteTag();

  const smartPlaylists: TagRulePlaylistRef[] = useMemo(
    () => (playlistData?.tagLists ?? []).map((p) => ({ listId: p.listId, title: p.title, tagRules: p.tagRules })),
    [playlistData]
  );

  const filtered = useMemo(
    () => TagManageUtils.filterUsages(usages, search, unusedOnly),
    [usages, search, unusedOnly]
  );
  const unusedCount = useMemo(() => usages.filter((u) => TagManageUtils.isUnused(u)).length, [usages]);

  // The scroll element lives inside Drawer children, which Mantine mounts in a
  // later commit (Transition) without re-rendering this component — a plain ref
  // leaves the virtualizer detached and the list empty on reopen when the tag
  // query is still fresh. State ref forces the re-render the virtualizer needs.
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollContainer,
    estimateSize: () => TAG_MANAGE.ROW_HEIGHT,
    overscan: TAG_MANAGE.OVERSCAN,
    getItemKey: (index) => filtered[index].tagId,
  });

  const deleteRefs = useMemo(
    () => (deleteTarget ? TagManageUtils.smartPlaylistRefs(smartPlaylists, deleteTarget.label) : []),
    [deleteTarget, smartPlaylists]
  );

  const handleRename = (tagId: number, label: string) => {
    const target = usages.find((u) => u.tagId === tagId);
    renameMutation.mutate(
      {
        tagId,
        label,
        hadMediaRefs: target ? TagManageUtils.totalMediaCount(target) > 0 : true,
        hadListRefs: target ? target.listCount > 0 : true,
      },
      {
        onSuccess: () => {
          setRenameTarget(null);
          notifications.show({ title: 'Tag renamed', message: `Now "${label}".`, color: 'green' });
        },
        onError: (err) => {
          notifications.show({ title: 'Rename failed', message: err.message, color: 'red' });
        },
      }
    );
  };

  const runDelete = (u: TagUsage) => {
    deleteMutation.mutate(
      { tagId: u.tagId },
      {
        onSuccess: (res) => {
          setDeleteTarget(null);
          const details =
            res.removedFromMediaCount > 0 || res.removedFromListCount > 0
              ? ` Removed from ${res.removedFromMediaCount} media and ${res.removedFromListCount} playlist rule(s).`
              : '';
          notifications.show({ title: 'Tag deleted', message: `"${u.label}" is gone.${details}`, color: 'green' });
        },
        onError: (err) => {
          notifications.show({ title: 'Delete failed', message: err.message, color: 'red' });
        },
      }
    );
  };

  const handleDeleteClick = (u: TagUsage) => {
    if (TagManageUtils.isUnused(u)) {
      modals.openConfirmModal({
        title: 'Delete tag?',
        children: (
          <Text size="sm">
            Delete the unused tag <Text span fw={600}>&ldquo;{u.label}&rdquo;</Text>? It is not on any
            media or playlist.
          </Text>
        ),
        labels: { confirm: 'Delete Tag', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => runDelete(u),
      });
    } else {
      setDeleteTarget(u);
    }
  };

  // "View affected" links: grid filter state lives in Zustand stores, so set
  // the filter/search directly and navigate — works from either page.
  const handleViewVideos = (label: string) => {
    useVideoGridState.getState().setFilters({ tagsAll: [label], tagsAny: [], tagsNone: [] });
    useVideoGridState.getState().setFiltersOpen(true);
    setDeleteTarget(null);
    onClose();
    void navigate({ to: '/videos' });
  };

  const handleViewStreams = (label: string) => {
    useStreamGridState.getState().setSearchText(label);
    setDeleteTarget(null);
    onClose();
    void navigate({ to: '/streams' });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Manage Tags"
      position="right"
      size="lg"
      padding="lg"
    >
      <Stack gap="sm">
        <Group gap="sm" wrap="nowrap">
          <TextInput
            placeholder="Search tags..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Switch
            label="Unused only"
            checked={unusedOnly}
            onChange={(e) => setUnusedOnly(e.currentTarget.checked)}
          />
        </Group>

        <Text size="sm" c="dimmed">
          {usages.length} tag{usages.length === 1 ? '' : 's'}
          {unusedCount > 0 && ` · ${unusedCount} unused`}
          {(search.trim().length > 0 || unusedOnly) && ` · ${filtered.length} shown`}
        </Text>

        {isError && (
          <Alert color="red" title="Could not load tags">
            Try closing and reopening this panel.
          </Alert>
        )}

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : filtered.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {usages.length === 0 ? 'No tags in this account yet.' : 'No tags match.'}
          </Text>
        ) : (
          <div ref={setScrollContainer} style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
            <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const u = filtered[virtualItem.index];
                const unused = TagManageUtils.isUnused(u);
                return (
                  <Group
                    key={u.tagId}
                    justify="space-between"
                    wrap="nowrap"
                    px="xs"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: virtualItem.size,
                      transform: `translateY(${virtualItem.start}px)`,
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                    }}
                  >
                    <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={500} truncate title={u.label}>
                        {u.label}
                      </Text>
                      <Group gap={6} wrap="nowrap">
                        <Text size="xs" c="dimmed" truncate>
                          {TagManageUtils.formatUsage(u)}
                        </Text>
                        {u.listCount > 0 && (
                          <Badge size="xs" variant="light" color="grape">
                            {u.listCount} playlist{u.listCount === 1 ? '' : 's'}
                          </Badge>
                        )}
                        {unused && (
                          <Badge size="xs" variant="light" color="gray">
                            Unused
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Rename">
                        <ActionIcon variant="subtle" color="gray" aria-label={`Rename tag ${u.label}`} onClick={() => setRenameTarget(u)}>
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon variant="subtle" color="red" aria-label={`Delete tag ${u.label}`} onClick={() => handleDeleteClick(u)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                );
              })}
            </div>
          </div>
        )}
      </Stack>

      <RenameTagModal
        usage={renameTarget}
        usages={usages}
        isPending={renameMutation.isPending}
        onClose={() => setRenameTarget(null)}
        onRename={handleRename}
      />

      <DeleteTagModal
        usage={deleteTarget}
        playlistRefs={deleteRefs}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onDelete={(tagId) => {
          const target = deleteTarget;
          if (target && target.tagId === tagId) runDelete(target);
        }}
        onViewVideos={handleViewVideos}
        onViewStreams={handleViewStreams}
      />
    </Drawer>
  );
}
